import { WizardFactory } from "@/shared/Wizard/services/WizardFactory";
import { BookText, Calculator, Cog, FileCog, FileDigit, Sparkles } from "lucide-vue-next";
import { h } from "vue";
import { AnalysisWizardStep } from "../models/AnalysisWizardStep";
import { WizardStepContent } from "@/shared/Wizard/models/WizardStepContent";
import DataCleaning from "@/components/DataCleaning/DataCleaning.vue";
import InputDataStep from "../InputDataStep/InputDataStep.vue";
import { AnalysisWizardModel } from "../models/AnalysisWizardModel";
import { AnalysisWizardSharedModel } from "../models/AnaylsisWizardSharedModel";
import { WizardStepStatus } from "@/shared/Wizard/models/WizardStepStatus";
import Calculations from "@/components/CustomCalculations/Calculations.vue";
import { AnalysisWizardStepType } from "../models/AnyalsisWizardStepType";
import AnalysisConfig from "@/components/AnalysisConfig/AnalysisConfig.vue";
import { Rule } from "@/components/DataCleaning/models/Rule";
import type { AnalysisConfig as IAnalysisConfig } from '@/components/AnalysisConfig/models/AnalysisConfig'
import { AnalysisConfigurationFactory } from "./AnalysisConfigurationFactory";
import InputDataStepStatus from "../InputDataStep/components/InputDataStepStatus.vue";
import AnalysisConfigStepStatus from "../AnalysisConfig/components/AnalysisConfigStepStatus.vue";
import DataProcessingStepStatus from "../DataProcessingStep/components/DataProcessingStepStatus.vue";
import { AplosFile } from "@/components/FileManager/models/AplosFile";
import CustomCalculationStepStatus from "../CustomCalculationsStep/components/CustomCalculationStepStatus.vue";
import { Execution } from "@/shared/Executions/models/Execution";
import { Calculation } from "@/components/CustomCalculations/models/Calculation";
import { Plot } from "@/components/Plotting/models/Plot";
import PostProcessingStep from "../PostProcessingStep/PostProcessingStep.vue";
import PostProcessingStepStatus from "../PostProcessingStep/components/PostProcessingStepStatus.vue";
import { Table } from "@/components/Tables/models/Table";
import CustomReports from "@/components/CustomReports/CustomReports.vue";
import { CustomReport } from "@/components/CustomReports/models/CustomReport";
import CustomReportStepStatus from "../CustomReportsStep/components/CustomReportStepStatus.vue";
import { PostProcessingOutputPlot } from "@/models/PostProcessingOutputPlot";
import { PostProcessingOutputTable } from "@/models/PostProcessingOutputTable";
import { PostProcessingOutput } from "@/models/PostProcessingOutput";


export class AnalysisWizardFactory extends WizardFactory<
    AnalysisWizardSharedModel,
    AnalysisWizardStep,
    AnalysisWizardModel> {

    static readonly injectableKey = 'AnalysisWizardFactory';

    get injectableKey() {
        return (this.constructor as typeof AnalysisWizardFactory).injectableKey;
    }

    public createWizardModel(): AnalysisWizardModel {

        const steps: Array<AnalysisWizardStep> = [

            (() => {
                const s = new AnalysisWizardStep(
                    'Input Data',
                    h(FileDigit),
                    new WizardStepContent<AnalysisWizardSharedModel>(
                        "Input Data",
                        InputDataStep,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                ['onUpdate:inputFile']: (val: AplosFile | undefined) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.inputFile = val;
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.InputData);
                                },
                                ['onUpdate:rules']: (val: Array<Rule>) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.rules = val;
                                },
                                ['onUpdate:analysisConfig']: (val: IAnalysisConfig | undefined) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.analysisConfig = val;
                                },
                            };
                        }
                    ),
                    AnalysisWizardStepType.InputData,
                    InputDataStepStatus
                );
                s.status = WizardStepStatus.InProgress;
                return s;
            })(),

            (() => {
                const s = new AnalysisWizardStep(
                    'Data Processing',
                    h(Sparkles),
                    new WizardStepContent<AnalysisWizardSharedModel>(
                        "Data Processing",
                        DataCleaning,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                fileSelectionDisabled: true,
                                analysisFile: wizardModel?.sharedModel?.inputFile,
                                cleanedFile: wizardModel?.sharedModel?.cleanedFile,
                                rules: wizardModel?.sharedModel?.rules,
                                dataCleaningExecution: wizardModel?.sharedModel?.dataCleaningExecution,
                                ['onUpdate:rules']: (val: Array<Rule> | undefined) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.rules = val;
                                },
                                ['onUpdate:cleanedFile']: (val: AplosFile | undefined) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.cleanedFile = val;
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.DataProcessing);
                                },
                                ['onUpdate:dataCleaningExecution']: (val: Execution) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.dataCleaningExecution = val;
                                },
                            };
                        }
                    ),
                    AnalysisWizardStepType.DataProcessing,
                    DataProcessingStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

            (() => {
                const s = new AnalysisWizardStep(
                    'PK Analysis',
                    h(Cog),
                    new WizardStepContent<AnalysisWizardSharedModel>(
                        "PK Analysis",
                        AnalysisConfig,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                mode: 'singleconfig',
                                wizardModel,
                                analysisFile: wizardModel?.sharedModel?.cleanedFile ?? wizardModel?.sharedModel?.inputFile,
                                analysisConfig: wizardModel?.sharedModel?.analysisConfig ?? AnalysisConfigurationFactory.createNewAnalysisConfiguration(),
                                analysisExecution: wizardModel?.sharedModel?.analysisExecution,
                                ['onUpdate:analysisConfig']: (val: IAnalysisConfig) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.analysisConfig = val
                                },
                                ['onUpdate:analysisExecution']: (val?: Execution) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.analysisExecution = val
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.AnalysisConfiguration);
                                }
                            };
                        }
                    ),
                    AnalysisWizardStepType.AnalysisConfiguration,
                    AnalysisConfigStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

            (() => {
                const s = new AnalysisWizardStep(
                    'Custom Calculations',
                    h(Calculator),
                    new WizardStepContent<AnalysisWizardSharedModel>(
                        "Calculations",
                        Calculations,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                customCalculations: wizardModel?.sharedModel?.customCalculations,
                                customCalculationsExecution: wizardModel?.sharedModel?.customCalculationsExecution,
                                analysisExecution: wizardModel?.sharedModel?.analysisExecution,
                                pkResultsFile: wizardModel?.sharedModel?.pkResultsFile,
                                sortColumns: wizardModel?.sharedModel?.customCalculationsSort,
                                analysisSelectionDisabled: true,
                                ['onUpdate:calculations']: (val: Array<Calculation>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customCalculations = val
                                },
                                ['onUpdate:sortColumns']: (val: Array<string>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customCalculationsSort = val
                                },
                                ['onUpdate:pkResultsFile']: (val: AplosFile) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.pkResultsFile = val
                                },
                                ['onUpdate:customCalculationsExecution']: (val: Execution) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customCalculationsExecution = val
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.CustomCalculations);

                                },
                                ['onUpdate:pkResultsCustomCalcsFile']: (val?: AplosFile) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.pkResultsCustomCalcsFile = val
                                }
                            };
                        }
                    ),
                    AnalysisWizardStepType.CustomCalculations,
                    CustomCalculationStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

            (() => {
                const s = new AnalysisWizardStep(
                    'Post Processing',
                    h(FileCog),
                    new WizardStepContent<AnalysisWizardSharedModel>(
                        "Post Processing",
                        PostProcessingStep,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                plots: wizardModel?.sharedModel?.plots,
                                tables: wizardModel?.sharedModel?.tables,
                                postProcessingExecution: wizardModel?.sharedModel?.postProcessingExecution,
                                analysisExecution: wizardModel?.sharedModel?.analysisExecution,
                                customCalculationExecution: wizardModel?.sharedModel?.customCalculationsExecution,
                                analysisFile: wizardModel?.sharedModel?.inputFile,
                                cleanedFile: wizardModel?.sharedModel?.cleanedFile,
                                pkResultsFile: wizardModel?.sharedModel?.pkResultsFile,
                                pkResultsCustomCalcsFile: wizardModel?.sharedModel?.pkResultsCustomCalcsFile,
                                wizardModel,
                                ['onUpdate:plots']: (val: Array<Plot>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.plots = val
                                },
                                ['onUpdate:tables']: (val: Array<Table>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.tables = val
                                },
                                ['onUpdate:postProcessingExecution']: (val: Execution) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.postProcessingExecution = val
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.PostProcessing);

                                },
                                ['onUpdate:pkResultsFile']: (val: AplosFile) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.pkResultsFile = val
                                },
                                ['onUpdate:pkResultsCustomCalcsFile']: (val?: AplosFile) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.pkResultsCustomCalcsFile = val
                                }
                            };
                        }
                    ),
                    AnalysisWizardStepType.PostProcessing,
                    PostProcessingStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

            (() => {
                const s = new AnalysisWizardStep(
                    'Reports',
                    h(BookText),
                    new WizardStepContent("Reports", CustomReports,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                mode: 'singleconfig',
                                postProcessingSelectionDisabled: true,
                                postProcessingExecution: wizardModel?.sharedModel?.postProcessingExecution,
                                customReports: wizardModel?.sharedModel?.customReports,
                                customReportsExecution: wizardModel?.sharedModel?.customReportsExecution,
                                customPlotsOutput: wizardModel?.sharedModel?.customPlotsOutput,
                                customTablesOutput: wizardModel?.sharedModel?.customTablesOutput,
                                ['onUpdate:customReports']: (val: Array<CustomReport>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customReports = val
                                },
                                ['onUpdate:customReportsExecution']: (val: Execution) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customReportsExecution = val
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AnalysisWizardStepType.Reports);
                                },
                                ['onUpdate:customPlotsOutput']: (val: PostProcessingOutput<PostProcessingOutputPlot>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customPlotsOutput = val
                                },
                                ['onUpdate:customTablesOutput']: (val: PostProcessingOutput<PostProcessingOutputTable>) => {
                                    if (!wizardModel?.sharedModel) return
                                    wizardModel.sharedModel.customTablesOutput = val
                                }
                            };
                        }
                    ),
                    AnalysisWizardStepType.Reports,
                    CustomReportStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

        ];

        const wizard = new AnalysisWizardModel(steps);
        wizard.activeStep = steps[0];

        return wizard;
    }

    private resetDependentSharedModelValues(sharedModel?: AnalysisWizardSharedModel, from?: AnalysisWizardStepType): void {
        if (!sharedModel || from === undefined) return;

        const ordered: AnalysisWizardStepType[] = [
            AnalysisWizardStepType.InputData,
            AnalysisWizardStepType.DataProcessing,
            AnalysisWizardStepType.AnalysisConfiguration,
            AnalysisWizardStepType.CustomCalculations,
            AnalysisWizardStepType.PostProcessing,
            AnalysisWizardStepType.Reports
        ];

        const resetMap: Record<string, (m: AnalysisWizardSharedModel) => void> = {
            [AnalysisWizardStepType.InputData]: (m: AnalysisWizardSharedModel) => {
                m.cleanedFile = undefined;
                m.rules = [];
                m.dataCleaningExecution = undefined;
            },
            [AnalysisWizardStepType.DataProcessing]: (m: AnalysisWizardSharedModel) => {
                m.analysisExecution = undefined;
                m.analysisConfig = undefined;
                m.pkResultsFile = undefined;
            },
            [AnalysisWizardStepType.AnalysisConfiguration]: (m: AnalysisWizardSharedModel) => {
                m.customCalculationsExecution = undefined;
                m.customCalculations = [];
                m.pkResultsCustomCalcsFile = undefined;
            },
            [AnalysisWizardStepType.CustomCalculations]: (m: AnalysisWizardSharedModel) => {
                m.postProcessingExecution = undefined;
                m.tables = [];
                m.plots = [];
            },
            [AnalysisWizardStepType.PostProcessing]: (m: AnalysisWizardSharedModel) => {
                m.customReportsExecution = undefined;
                m.customPlotsOutput = undefined;
                m.customTablesOutput = undefined;
                m.customReports = [];
            },
            //@ts-ignore
            [AnalysisWizardStepType.Reports]: (m: AnalysisWizardSharedModel) => { /* nothing to clear downstream */ }
        };

        const start = ordered.indexOf(from);
        if (start < 0) return;
        for (let i = start; i < ordered.length; i++) {
            const key = ordered[i];
            const fn = resetMap[key];
            if (fn) fn(sharedModel);
        }
    }

}